import React, { createContext, useState, useContext, useEffect } from 'react';

// Import des traductions
import fr from '../locales/fr.json';
import en from '../locales/en.json';
import es from '../locales/es.json';
import ar from '../locales/ar.json';

// Langues disponibles
export const languages = [
  { code: 'fr', label: 'Français', flag: 'https://flagcdn.com/w20/fr.png', dir: 'ltr' },
  { code: 'en', label: 'English', flag: 'https://flagcdn.com/w20/gb.png', dir: 'ltr' },
  { code: 'es', label: 'Español', flag: 'https://flagcdn.com/w20/es.png', dir: 'ltr' },
  { code: 'ar', label: 'العربية', flag: 'https://flagcdn.com/w20/ma.png', dir: 'rtl' },
];

// Map des traductions
const translations = { fr, en, es, ar };

// Création du contexte
const LanguageContext = createContext();

// Provider
export const LanguageProvider = ({ children }) => {
  // Récupère la langue du localStorage ou utilise le français par défaut
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('carvy-language');
    return saved ? languages.find(l => l.code === saved) || languages[0] : languages[0];
  });

  // Sauvegarde la langue dans le localStorage
  useEffect(() => {
    localStorage.setItem('carvy-language', currentLanguage.code);
    // Gère la direction RTL pour l'arabe
    document.documentElement.dir = currentLanguage.dir;
    document.documentElement.lang = currentLanguage.code;
  }, [currentLanguage]);

  // Fonction de traduction
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[currentLanguage.code];
    
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback vers le français si la clé n'existe pas
        let fallback = translations['fr'];
        for (const fk of keys) {
          if (fallback && fallback[fk] !== undefined) {
            fallback = fallback[fk];
          } else {
            return key; // Retourne la clé si non trouvée
          }
        }
        value = fallback;
        break;
      }
    }
    
    // Remplacer les paramètres dans la chaîne
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      Object.keys(params).forEach(paramKey => {
        value = value.replace(new RegExp(`{{${paramKey}}}`, 'g'), params[paramKey]);
      });
    }
    
    return value;
  };

  // Change la langue
  const changeLanguage = (langCode) => {
    const lang = languages.find(l => l.code === langCode);
    if (lang) {
      setCurrentLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      languages,
      t,
      changeLanguage,
      isRTL: currentLanguage.dir === 'rtl'
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook personnalisé
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;

